import { HOC_MDXWrapper } from 'nextra/setup-page'
import { pageMap } from '/vercel/path0/.next/static/chunks/nextra-page-map-.mjs'
/*@jsxRuntime automatic*/
/*@jsxImportSource react*/
import {useMDXComponents as _provideComponents} from "nextra/mdx";
const title = "Verify Proofs";
const frontMatter = {
  "title": "Verify Proofs",
  "description": "Pluto — The API for personal web data. Embed it in your app to authenticate users, share data and perform actions on their behalf for any site on the internet."
};
import {Callout} from "nextra/components";
import VerifyProofs from "../components/VerifyProofs";
export function useTOC(props) {
  return [{
    value: "Verification Methods",
    id: "verification-methods",
    depth: 2
  }, {
    value: "Pluto Notary API Verification",
    id: "pluto-notary-api-verification",
    depth: 2
  }, {
    value: "API Endpoint",
    id: "api-endpoint",
    depth: 3
  }, {
    value: "Request Format",
    id: "request-format",
    depth: 3
  }, {
    value: "Response Format",
    id: "response-format",
    depth: 3
  }, {
    value: "Smart Contract Verification",
    id: "smart-contract-verification",
    depth: 2
  }, {
    value: "Try it out",
    id: "try-it-out",
    depth: 3
  }, {
    value: "Contract Function Signature",
    id: "contract-function-signature",
    depth: 3
  }, {
    value: "Verification Logic",
    id: "verification-logic",
    depth: 3
  }, {
    value: "Step-by-Step Verification",
    id: "step-by-step-verification",
    depth: 3
  }, {
    value: "Step 1: Extract Data from Pluto Attestation",
    id: "step-1-extract-data-from-pluto-attestation",
    depth: 4
  }, {
    value: "Step 2: Understand the Data Flow",
    id: "step-2-understand-the-data-flow",
    depth: 4
  }, {
    value: "Step 3: Prepare Function Arguments",
    id: "step-3-prepare-function-arguments",
    depth: 4
  }, {
    value: "Step 4: Format for Transaction",
    id: "step-4-format-for-transaction",
    depth: 4
  }, {
    value: "Common Mistakes to Avoid",
    id: "common-mistakes-to-avoid",
    depth: 2
  }, {
    value: "Incorrect Struct Assembly",
    id: "incorrect-struct-assembly",
    depth: 3
  }, {
    value: "Proof Data Ordering",
    id: "proof-data-ordering",
    depth: 3
  }, {
    value: "String Field Formatting",
    id: "string-field-formatting",
    depth: 3
  }, {
    value: "Script Content Issues",
    id: "script-content-issues",
    depth: 3
  }, {
    value: "V Parameter Confusion",
    id: "v-parameter-confusion",
    depth: 3
  }, {
    value: "Notary Validation",
    id: "notary-validation",
    depth: 3
  }, {
    value: "Duplicate Proof Submission",
    id: "duplicate-proof-submission",
    depth: 3
  }, {
    value: "Hash Recreation Errors",
    id: "hash-recreation-errors",
    depth: 3
  }, {
    value: "Endianness Issues",
    id: "endianness-issues",
    depth: 3
  }];
}
function MDXLayout(props) {
  const {toc = useTOC(props)} = props;
  const _components = {
    a: "a",
    code: "code",
    details: "details",
    em: "em",
    h1: "h1",
    h2: "h2",
    h3: "h3",
    h4: "h4",
    li: "li",
    ol: "ol",
    p: "p",
    pre: "pre",
    span: "span",
    strong: "strong",
    summary: "summary",
    ul: "ul",
    ..._provideComponents(),
    ...props.components
  };
  return <><_components.h1>{"Verify Proofs"}</_components.h1>{"\n"}<_components.p>{"Pluto’s attestation system creates cryptographic proofs that a specific script was executed at a specific time with specific data. The signature verification process ensures the integrity of both the execution environment (script) and the execution context (session). This guide shows how to interpret and verify Pluto notary signatures."}</_components.p>{"\n"}<_components.h2 id={toc[0].id}>{toc[0].value}</_components.h2>{"\n"}<_components.p>{"There are two primary ways to verify Pluto attestations:"}</_components.p>{"\n"}<_components.ol>{"\n"}<_components.li>{"Pluto Notary API Verification: Quick, off-chain verification using Pluto’s notary service API."}</_components.li>{"\n"}<_components.li>{"Smart Contract Verification: On-chain verification using the deployed smart contract on Base Sepolia."}</_components.li>{"\n"}</_components.ol>{"\n"}<_components.h2 id={toc[1].id}>{toc[1].value}</_components.h2>{"\n"}<_components.p>{"The simplest way to verify a Pluto attestation is using the notary API endpoint. This method is ideal for applications that need quick verification without blockchain interaction."}</_components.p>{"\n"}<_components.h3 id={toc[2].id}>{toc[2].value}</_components.h3>{"\n"}<_components.pre tabIndex="0" data-language="plaintext" data-word-wrap=""><_components.code><_components.span><_components.span>{"POST https://notary.pluto.xyz/verify"}</_components.span></_components.span></_components.code></_components.pre>{"\n"}<_components.h3 id={toc[3].id}>{toc[3].value}</_components.h3>{"\n"}<_components.p>{"The API expects a JSON payload with the proof data wrapped in an attestation object."}</_components.p>{"\n"}<_components.pre tabIndex="0" data-language="javascript" data-word-wrap=""><_components.code><_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"const"}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{" verifyAttestation"}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" ="}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" async"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" ("}</_components.span><_components.span style={{
    "--shiki-light": "#E36209",
    "--shiki-dark": "#FFAB70"
  }}>{"attestationData"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{") "}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"=>"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"  const"}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{" response"}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" ="}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" await"}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{" fetch"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"'https://notary.pluto.xyz/verify'"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    method: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"'POST'"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    headers: {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"      'Content-Type'"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{": "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"'application/json'"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    },"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    body: "}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"JSON"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"stringify"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"({"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"      attestation: attestationData"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    })"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  });"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  "}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"  const"}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{" result"}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" ="}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" await"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" response."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"json"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"();"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  "}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"  if"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" (result.status "}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"==="}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{" 'success'"}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" &&"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" result.verified) {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    console."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"log"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"'✅ Attestation verified successfully'"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"    return"}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{" true"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{";"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  } "}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"else"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    console."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"log"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"'❌ Attestation verification failed:'"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", result.message);"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"    return"}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{" false"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{";"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  }"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"};"}</_components.span></_components.span></_components.code></_components.pre>{"\n"}<_components.h3 id={toc[4].id}>{toc[4].value}</_components.h3>{"\n"}<_components.p>{"Success Response:"}</_components.p>{"\n"}<_components.pre tabIndex="0" data-language="json" data-word-wrap=""><_components.code><_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"{"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"  \"status\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{": "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"success\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"}"}</_components.span></_components.span></_components.code></_components.pre>{"\n"}<_components.h2 id={toc[5].id}>{toc[5].value}</_components.h2>{"\n"}<Callout type="info"><_components.p>{"Signature verification testnet contract is deployed on Base Sepolia at\n"}<_components.a href="https://sepolia.basescan.org/address/0xeB3Ba25400A4d716d091090942705aD51d04B7ea">{"0xeB3Ba25400A4d716d091090942705aD51d04B7ea"}</_components.a>{"."}</_components.p></Callout>{"\n"}<_components.h3 id={toc[6].id}>{toc[6].value}</_components.h3>{"\n"}{"\n"}<VerifyProofs />{"\n"}<_components.h3 id={toc[7].id}>{toc[7].value}</_components.h3>{"\n"}<Callout type="info"><_components.p>{"Contract can be found at the\n"}<_components.a href="https://github.com/pluto/solidity-verifier">{"solidity-verifier"}</_components.a>{" repo. Please\nreach out to us, if you encounter or discover any issue in the verification."}</_components.p></Callout>{"\n"}<_components.pre tabIndex="0" data-language="solidity" data-word-wrap=""><_components.code><_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"function"}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{" verifyAttestation"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"    AttestationInput"}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" memory"}</_components.span><_components.span style={{
    "--shiki-light": "#E36209",
    "--shiki-dark": "#FFAB70"
  }}>{" input"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"    AttestationSignature"}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" memory"}</_components.span><_components.span style={{
    "--shiki-light": "#E36209",
    "--shiki-dark": "#FFAB70"
  }}>{" signature"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{") "}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"public"}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" returns"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" ("}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"bool"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{")"}</_components.span></_components.span></_components.code></_components.pre>{"\n"}<_components.p>{"Where the structs are defined as:"}</_components.p>{"\n"}<_components.pre tabIndex="0" data-language="solidity" data-word-wrap=""><_components.code><_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"struct"}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{" AttestationInput"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"    string"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" version;      "}</_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{"// Protocol version (e.g., \"v1\")"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"    string"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" scriptRaw;    "}</_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{"// The raw script content that was executed"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"    string"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" issuedAt;     "}</_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{"// RFC3339 timestamp when attestation was created"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"    string"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" nonce;        "}</_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{"// Random nonce for replay protection"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"    string"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" sessionId;    "}</_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{"// UUID identifying the execution session"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    ProofData[] data;    "}</_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{"// Key-value pairs of proof data"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"}"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"struct"}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{" AttestationSignature"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"    bytes32"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" digest;         "}</_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{"// Hash of the attestation data"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"    uint8"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" v;               "}</_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{"// Recovery parameter (27 or 28)"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"    bytes32"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" r;             "}</_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{"// First 32 bytes of signature"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"    bytes32"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" s;             "}</_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{"// Second 32 bytes of signature"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"    address"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" expectedSigner; "}</_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{"// Expected signer address (must be a registered notary)"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"}"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"struct"}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{" ProofData"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"    string"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" key;   "}</_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{"// The key for the proof data"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"    string"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" value; "}</_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{"// The value for the proof data"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"}"}</_components.span></_components.span></_components.code></_components.pre>{"\n"}<_components.h3 id={toc[8].id}>{toc[8].value}</_components.h3>{"\n"}<_components.p>{"The contract performs these critical checks:"}</_components.p>{"\n"}<_components.ol>{"\n"}<_components.li><_components.strong>{"Calculate and Verify Hashes"}</_components.strong></_components.li>{"\n"}</_components.ol>{"\n"}<_components.pre tabIndex="0" data-language="solidity" data-word-wrap=""><_components.code><_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{"// Calculate script hash"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"bytes32"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" scriptHash "}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"="}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{" calculateScriptHash"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"(input.version, input.scriptRaw);"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{"// Calculate session hash"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"bytes32"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" sessionHash "}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"="}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{" calculateSessionHash"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    input.version, input.issuedAt, input.nonce, input.sessionId, input.data"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{"// Calculate digest"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"bytes32"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" digest "}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"="}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{" calculateDigest"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"(sessionHash, scriptHash);"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#6A737D",
    "--shiki-dark": "#6A737D"
  }}>{"// Verify the digest matches"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"if"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" (digest "}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"!="}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" signature.digest) {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"    return"}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{" false"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{";"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"}"}</_components.span></_components.span></_components.code></_components.pre>{"\n"}<_components.ol start="2">{"\n"}<_components.li><_components.strong>{"Verify Signature via Verifier Contract"}</_components.strong></_components.li>{"\n"}</_components.ol>{"\n"}<_components.p>{"The "}<_components.code>{"verifyAttestation"}</_components.code>{" function calls the underlying "}<_components.code>{"Verifier"}</_components.code>{" contract which performs:"}</_components.p>{"\n"}<_components.ul>{"\n"}<_components.li><_components.strong>{"Notary Check"}</_components.strong>{": Verifies the signer is a registered notary"}</_components.li>{"\n"}<_components.li><_components.strong>{"Digest Verification"}</_components.strong>{": Ensures digest = keccak256(sessionHash || scriptHash)"}</_components.li>{"\n"}<_components.li><_components.strong>{"Signature Recovery"}</_components.strong>{": Uses ecrecover to verify the signature"}</_components.li>{"\n"}<_components.li><_components.strong>{"Replay Protection"}</_components.strong>{": Prevents duplicate submissions per wallet"}</_components.li>{"\n"}</_components.ul>{"\n"}<_components.pre tabIndex="0" data-language="solidity" data-word-wrap=""><_components.code><_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"bool"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" success "}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"="}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" verifier."}</_components.span><_components.span style={{
    "--shiki-light": "#6F42C1",
    "--shiki-dark": "#B392F0"
  }}>{"verifyNotarySignature"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"("}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    signature.digest, signature.v, signature.r, signature.s,"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"    signature.expectedSigner, scriptHash, sessionHash"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{");"}</_components.span></_components.span></_components.code></_components.pre>{"\n"}<_components.h3 id={toc[9].id}>{toc[9].value}</_components.h3>{"\n"}<_components.h4 id={toc[10].id}>{toc[10].value}</_components.h4>{"\n"}<_components.p>{"From the Pluto attestation JSON, you need these fields:"}</_components.p>{"\n"}<_components.pre tabIndex="0" data-language="json" data-word-wrap=""><_components.code><_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"{"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"  \"version\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{": "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"v1\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"  \"script\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{": {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"    \"raw\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{": "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"import { createSession } from '@plutoxyz/automation';"}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"\\n"}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"...\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  },"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"  \"issued_at\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{": "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"2025-07-03T15:03:57Z\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"  \"nonce\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{": "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"0x3935659ffb235e15\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"  \"session_id\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{": "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"8bc45a6a-7b61-4828-a513-f6913535547a\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"  \"data\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{": {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"    \"bank_balance\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{": "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"8910.3\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  },"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"  \"digest\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{": "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"0x2843da5e7e228b273bb71b3a020dbc62799b5b4c032de361a4783f57f1223809\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"  \"signature_r\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{": "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"0x53997b7d7a63a6e86c37f8d31a2a049303678afe69672e74368b6221ab56ee81\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"  \"signature_s\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{": "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"0x5e7c985d30fde9589ad38617f09cac0814a4b385c5436b6805896d33dc49efaf\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"  \"signature_v\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{": "}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"28"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"  \"signer\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{": "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"0x209Af77DfDaba352890b0Bc9B86A25bE67eF436A\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"}"}</_components.span></_components.span></_components.code></_components.pre>{"\n"}<_components.h4 id={toc[11].id}>{toc[11].value}</_components.h4>{"\n"}<_components.p>{"The attestation contains pre-computed hashes, but understanding their creation helps with verification:"}</_components.p>{"\n"}<_components.ol>{"\n"}<_components.li><_components.strong>{"Script Hash"}</_components.strong>{": Hash of version + script content"}</_components.li>{"\n"}</_components.ol>{"\n"}<_components.ul>{"\n"}<_components.li><_components.strong>{"Version"}</_components.strong>{": Protocol version string (like “v1”) - ensures compatibility across protocol upgrades"}</_components.li>{"\n"}<_components.li><_components.strong>{"Script Raw Content"}</_components.strong>{": The actual executable code/script that was run"}</_components.li>{"\n"}</_components.ul>{"\n"}<_components.pre tabIndex="0" data-language="plaintext" data-word-wrap=""><_components.code><_components.span><_components.span>{"script_hash = Keccak256(version + script)"}</_components.span></_components.span></_components.code></_components.pre>{"\n"}<_components.ol start="2">{"\n"}<_components.li><_components.strong>{"Session Hash"}</_components.strong>{": Hash of version + timestamp + nonce + session ID + proof data"}</_components.li>{"\n"}</_components.ol>{"\n"}<_components.ul>{"\n"}<_components.li><_components.strong>{"Version"}</_components.strong>{": Same protocol version as script hash"}</_components.li>{"\n"}<_components.li><_components.strong>{"IssuedAt"}</_components.strong>{": RFC3339 formatted timestamp of when the attestation was created"}</_components.li>{"\n"}<_components.li><_components.strong>{"Nonce"}</_components.strong>{": 8 random bytes (hex encoded with 0x prefix) to prevent replay attacks"}</_components.li>{"\n"}<_components.li><_components.strong>{"SessionId"}</_components.strong>{": UUID string identifying this specific execution session"}</_components.li>{"\n"}<_components.li><_components.strong>{"Data"}</_components.strong>{": Key-value pairs of proof data, "}<_components.em>{"sorted"}</_components.em>{" by key for deterministic hashing"}</_components.li>{"\n"}</_components.ul>{"\n"}<_components.pre tabIndex="0" data-language="plaintext" data-word-wrap=""><_components.code><_components.span><_components.span>{"session_hash = Keccak256(version + issued_at + nonce + session_id + prove_data)"}</_components.span></_components.span></_components.code></_components.pre>{"\n"}<_components.ol start="3">{"\n"}<_components.li><_components.strong>{"Report Data"}</_components.strong>{": session_hash + script_hash (64 bytes)"}</_components.li>{"\n"}<_components.li><_components.strong>{"Digest"}</_components.strong>{": Hash of report data"}</_components.li>{"\n"}<_components.li><_components.strong>{"Signature"}</_components.strong>{": ECDSA signature of digest"}</_components.li>{"\n"}</_components.ol>{"\n"}<_components.h4 id={toc[12].id}>{toc[12].value}</_components.h4>{"\n"}<_components.p>{"Map the attestation fields to the struct parameters:"}</_components.p>{"\n"}<_components.p><_components.strong>{"AttestationInput struct:"}</_components.strong></_components.p>{"\n"}<_components.pre tabIndex="0" data-language="plaintext" data-word-wrap=""><_components.code><_components.span><_components.span>{"version:   = attestation.version"}</_components.span></_components.span>{"\n"}<_components.span><_components.span>{"scriptRaw: = attestation.script.raw"}</_components.span></_components.span>{"\n"}<_components.span><_components.span>{"issuedAt:  = attestation.issued_at"}</_components.span></_components.span>{"\n"}<_components.span><_components.span>{"nonce:     = attestation.nonce"}</_components.span></_components.span>{"\n"}<_components.span><_components.span>{"sessionId: = attestation.session_id"}</_components.span></_components.span>{"\n"}<_components.span><_components.span>{"data:      = [{key: \"bank_balance\", value: \"8910.3\"}] // from attestation.data"}</_components.span></_components.span></_components.code></_components.pre>{"\n"}<_components.p><_components.strong>{"AttestationSignature struct:"}</_components.strong></_components.p>{"\n"}<_components.pre tabIndex="0" data-language="plaintext" data-word-wrap=""><_components.code><_components.span><_components.span>{"digest:         = attestation.digest"}</_components.span></_components.span>{"\n"}<_components.span><_components.span>{"v:              = attestation.signature_v"}</_components.span></_components.span>{"\n"}<_components.span><_components.span>{"r:              = attestation.signature_r"}</_components.span></_components.span>{"\n"}<_components.span><_components.span>{"s:              = attestation.signature_s"}</_components.span></_components.span>{"\n"}<_components.span><_components.span>{"expectedSigner: = attestation.signer"}</_components.span></_components.span></_components.code></_components.pre>{"\n"}<_components.h4 id={toc[13].id}>{toc[13].value}</_components.h4>{"\n"}<_components.p>{"Ensure proof data is sorted by key alphabetically and properly formatted:"}</_components.p>{"\n"}<_components.pre tabIndex="0" data-language="javascript" data-word-wrap=""><_components.code><_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"const"}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{" attestationInput"}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" ="}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  version: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"v1\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  scriptRaw: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"import { createSession } from '@plutoxyz/automation';"}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"\\n"}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"...\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  issuedAt: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"2025-07-03T15:03:57Z\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  nonce: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"0x3935659ffb235e15\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  sessionId: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"8bc45a6a-7b61-4828-a513-f6913535547a\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  data: [{ key: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"bank_balance\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{", value: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"8910.3\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" }],"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"};"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{"const"}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{" attestationSignature"}</_components.span><_components.span style={{
    "--shiki-light": "#D73A49",
    "--shiki-dark": "#F97583"
  }}>{" ="}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{" {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  digest: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"0x2843da5e7e228b273bb71b3a020dbc62799b5b4c032de361a4783f57f1223809\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  v: "}</_components.span><_components.span style={{
    "--shiki-light": "#005CC5",
    "--shiki-dark": "#79B8FF"
  }}>{"28"}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  r: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"0x53997b7d7a63a6e86c37f8d31a2a049303678afe69672e74368b6221ab56ee81\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  s: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"0x5e7c985d30fde9589ad38617f09cac0814a4b385c5436b6805896d33dc49efaf\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"  expectedSigner: "}</_components.span><_components.span style={{
    "--shiki-light": "#032F62",
    "--shiki-dark": "#9ECBFF"
  }}>{"\"0x209Af77DfDaba352890b0Bc9B86A25bE67eF436A\""}</_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-light": "#24292E",
    "--shiki-dark": "#E1E4E8"
  }}>{"};"}</_components.span></_components.span></_components.code></_components.pre>{"\n"}<_components.h2 id={toc[14].id}>{toc[14].value}</_components.h2>{"\n"}<_components.details><_components.summary>{"Click to expand "}</_components.summary><_components.h3 id={toc[15].id}>{toc[15].value}</_components.h3><_components.p>{"The most common mistake is incorrectly assembling the "}<_components.code>{"AttestationInput"}</_components.code>{" and "}<_components.code>{"AttestationSignature"}</_components.code>{" structs:"}</_components.p><_components.ul>{"\n"}<_components.li>{"Ensure all string fields are exactly as they appear in the original attestation"}</_components.li>{"\n"}<_components.li>{"Proof data must be sorted alphabetically by key for deterministic hashing"}</_components.li>{"\n"}<_components.li>{"Don’t modify script content, timestamps, or other fields"}</_components.li>{"\n"}</_components.ul><_components.h3 id={toc[16].id}>{toc[16].value}</_components.h3><_components.ul>{"\n"}<_components.li>{"The "}<_components.code>{"data"}</_components.code>{" array in "}<_components.code>{"AttestationInput"}</_components.code>{" must have keys sorted alphabetically"}</_components.li>{"\n"}<_components.li>{"This ensures deterministic hash calculation across different implementations"}</_components.li>{"\n"}<_components.li>{"Original attestation order may differ from sorted order required for verification"}</_components.li>{"\n"}</_components.ul><_components.h3 id={toc[17].id}>{toc[17].value}</_components.h3><_components.ul>{"\n"}<_components.li><_components.code>{"issuedAt"}</_components.code>{" must be in RFC3339 format exactly as in the attestation"}</_components.li>{"\n"}<_components.li><_components.code>{"nonce"}</_components.code>{" should include the "}<_components.code>{"0x"}</_components.code>{" prefix if present in the original"}</_components.li>{"\n"}<_components.li><_components.code>{"sessionId"}</_components.code>{" must be the exact UUID string"}</_components.li>{"\n"}<_components.li><_components.code>{"version"}</_components.code>{" should match exactly (typically “v1”)"}</_components.li>{"\n"}</_components.ul><_components.h3 id={toc[18].id}>{toc[18].value}</_components.h3><_components.ul>{"\n"}<_components.li>{"The "}<_components.code>{"scriptRaw"}</_components.code>{" field must contain the exact script content including newlines"}</_components.li>{"\n"}<_components.li>{"Don’t modify or reformat the script code"}</_components.li>{"\n"}<_components.li>{"Preserve all whitespace and formatting from the original attestation"}</_components.li>{"\n"}</_components.ul><_components.h3 id={toc[19].id}>{toc[19].value}</_components.h3><_components.ul>{"\n"}<_components.li>{"Pluto uses Ethereum’s recovery parameter convention (27 or 28)"}</_components.li>{"\n"}<_components.li>{"Your example shows "}<_components.code>{"signature_v: 28"}</_components.code>{" - pass this value directly"}</_components.li>{"\n"}<_components.li>{"Don’t subtract 27 - pass the value as-is"}</_components.li>{"\n"}</_components.ul><_components.h3 id={toc[20].id}>{toc[20].value}</_components.h3><_components.ul>{"\n"}<_components.li>{"The underlying verifier contract only accepts signatures from registered notaries"}</_components.li>{"\n"}<_components.li>{"Ensure the "}<_components.code>{"expectedSigner"}</_components.code>{" address is a trusted Pluto notary"}</_components.li>{"\n"}<_components.li>{"Invalid notary addresses will cause the transaction to revert"}</_components.li>{"\n"}</_components.ul><_components.h3 id={toc[21].id}>{toc[21].value}</_components.h3><_components.ul>{"\n"}<_components.li>{"The contract prevents replay attacks by tracking used proofs per wallet"}</_components.li>{"\n"}<_components.li>{"Each proof can only be submitted once by the same wallet address"}</_components.li>{"\n"}<_components.li>{"Different wallets CAN submit the same proof (only same wallet + same proof is blocked)"}</_components.li>{"\n"}<_components.li>{"Attempting to resubmit from the same wallet will cause "}<_components.code>{"DuplicateProof()"}</_components.code>{" revert"}</_components.li>{"\n"}</_components.ul><_components.h3 id={toc[22].id}>{toc[22].value}</_components.h3><_components.p>{"If you’re recreating hashes for verification:"}</_components.p><_components.ul>{"\n"}<_components.li>{"Use Keccak256 (not SHA3-256) - they’re different algorithms"}</_components.li>{"\n"}<_components.li>{"Maintain exact byte order when concatenating"}</_components.li>{"\n"}<_components.li>{"Sort proof data keys alphabetically"}</_components.li>{"\n"}<_components.li>{"Use exact string formatting (RFC3339 for timestamps, hex with 0x for nonces)"}</_components.li>{"\n"}</_components.ul><_components.h3 id={toc[23].id}>{toc[23].value}</_components.h3><_components.ul>{"\n"}<_components.li>{"All values are big-endian (most significant byte first)"}</_components.li>{"\n"}<_components.li>{"Don’t reverse byte order when converting between formats"}</_components.li>{"\n"}</_components.ul></_components.details></>;
}


export default HOC_MDXWrapper(
  MDXLayout,
  '/verify-proofs',
  {"filePath":"pages/verify-proofs.mdx","timestamp":1752851622000,pageMap,frontMatter,title},
  typeof RemoteContent === 'undefined' ? useTOC : RemoteContent.useTOC
)